/**
 * Seeds mock data for development/demo mode when no data exists.
 * Creates a complete hierarchy: Organization -> Project -> Environment -> Instances
 */

// Consistent IDs used across all mock data
export const MOCK_IDS = {
  organization: 'mock-org-gizmodata',
  project: 'mock-project-main',
  environment: 'mock-env-production',
  cloudRegion: 'azure-eastus',
  user: 'mock-user-1',
  // Plan IDs
  planFree: 'mock-plan-free',
  planStandard: 'mock-plan-standard',
  planEnterprise: 'mock-plan-enterprise',
} as const;

// Storage keys
const STORAGE_KEYS = {
  organizations: 'gizmodata-organizations',
  currentOrganization: 'gizmodata-current-organization',
  members: 'gizmodata-members',
  projects: 'gizmodata-projects',
  environments: 'gizmodata-environments',
  clusters: 'gizmosql-clusters',
  invites: 'gizmodata-invites',
  plans: 'gizmodata-plans',
  secretsAwsS3: 'gizmodata-secrets-aws-s3',
  secretsCloudflareR2: 'gizmodata-secrets-cloudflare-r2',
  secretsGcs: 'gizmodata-secrets-gcs',
  secretsAzureStorage: 'gizmodata-secrets-azure-storage',
  secretsPostgresql: 'gizmodata-secrets-postgresql',
  secretsAdbc: 'gizmodata-secrets-adbc',
  seeded: 'gizmodata-mock-seeded',
} as const;

export { STORAGE_KEYS };

interface MockPlan {
  id: string;
  name: string;
  label: string;
  description: string;
  maxMembers: number | null;
  maxCpuQuota: number | null;
  maxStorageGb: number | null;
  pricePerMonthUsd: number;
  includesComputeCost: boolean;
  includesStorageCost: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface MockOrganization {
  id: string;
  name: string;
  label: string;
  domain: string;
  planId: string;
  defaultCloudRegionId?: string;
  createdByUserId: string;
  createdAt: string;
  updatedByUserId: string;
  updatedAt: string;
  memberCount?: number;
}

interface MockProject {
  id: string;
  name: string;
  label: string;
  organizationId: string;
  defaultCloudRegionId?: string;
  createdByUserId: string;
  createdAt: string;
  updatedByUserId: string;
  updatedAt: string;
}

interface MockEnvironment {
  id: string;
  name: string;
  label: string;
  projectId: string;
  defaultCloudRegionId?: string;
  createdByUserId: string;
  createdAt: string;
  updatedByUserId: string;
  updatedAt: string;
}

interface MockMember {
  id: string;
  organizationId: string;
  userId: string;
  email: string;
  role: 'admin' | 'member';
  joinedAt: string;
}

// Mock plans
const mockPlans: MockPlan[] = [
  {
    id: MOCK_IDS.planFree,
    name: 'free',
    label: 'Free Plan',
    description: 'Perfect for individuals and small teams getting started with GizmoData. Includes up to 5 organization members, 0.5 CPU quota, and 10GB storage.',
    maxMembers: 5,
    maxCpuQuota: 0.5,
    maxStorageGb: 10,
    pricePerMonthUsd: 0,
    includesComputeCost: false,
    includesStorageCost: false,
    isActive: true,
    sortOrder: 1,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: MOCK_IDS.planStandard,
    name: 'standard',
    label: 'Standard Plan',
    description: 'For growing teams that need more power. Includes up to 10 organization members, 16 CPU quota, and unlimited storage. Compute and storage costs are billed separately based on usage.',
    maxMembers: 10,
    maxCpuQuota: 16,
    maxStorageGb: null, // Unlimited
    pricePerMonthUsd: 25,
    includesComputeCost: false,
    includesStorageCost: false,
    isActive: true,
    sortOrder: 2,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: MOCK_IDS.planEnterprise,
    name: 'enterprise',
    label: 'Enterprise Plan',
    description: 'For large organizations with demanding workloads. Includes unlimited organization members, unlimited CPU quota, and unlimited storage. Compute and storage costs are billed separately based on usage.',
    maxMembers: null, // Unlimited
    maxCpuQuota: null, // Unlimited
    maxStorageGb: null, // Unlimited
    pricePerMonthUsd: 100,
    includesComputeCost: false,
    includesStorageCost: false,
    isActive: true,
    sortOrder: 3,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
];

const mockOrganization: MockOrganization = {
  id: MOCK_IDS.organization,
  name: 'GizmoData Demo',
  label: 'gizmodata-demo',
  domain: 'demo.gizmodata.com',
  planId: MOCK_IDS.planStandard, // Demo org uses Standard plan
  defaultCloudRegionId: MOCK_IDS.cloudRegion,
  createdByUserId: MOCK_IDS.user,
  createdAt: '2024-01-01T00:00:00Z',
  updatedByUserId: MOCK_IDS.user,
  updatedAt: '2024-01-01T00:00:00Z',
  memberCount: 1,
};

const mockProject: MockProject = {
  id: MOCK_IDS.project,
  name: 'Main Project',
  label: 'main-project',
  organizationId: MOCK_IDS.organization,
  defaultCloudRegionId: MOCK_IDS.cloudRegion,
  createdByUserId: MOCK_IDS.user,
  createdAt: '2024-01-01T00:00:00Z',
  updatedByUserId: MOCK_IDS.user,
  updatedAt: '2024-01-01T00:00:00Z',
};

const mockEnvironment: MockEnvironment = {
  id: MOCK_IDS.environment,
  name: 'Production',
  label: 'production',
  projectId: MOCK_IDS.project,
  defaultCloudRegionId: MOCK_IDS.cloudRegion,
  createdByUserId: MOCK_IDS.user,
  createdAt: '2024-01-01T00:00:00Z',
  updatedByUserId: MOCK_IDS.user,
  updatedAt: '2024-01-01T00:00:00Z',
};

const mockMember: MockMember = {
  id: 'mock-member-1',
  organizationId: MOCK_IDS.organization,
  userId: MOCK_IDS.user,
  email: 'demo@gizmodata.com',
  role: 'admin',
  joinedAt: '2024-01-01T00:00:00Z',
};

// Mock secrets for demo mode
const mockAwsS3Secrets = [
  {
    id: 'aws-s3-prod',
    name: 'Production S3 Bucket',
    description: 'Main production S3 bucket for file storage',
    accessKeyId: 'AKIAIOSFODNN7EXAMPLE',
    secretAccessKey: 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY',
    region: 'us-east-1',
    bucketName: 'my-prod-bucket',
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    scope: 'organization',
    organizationId: MOCK_IDS.organization,
  },
];

const mockCloudflareR2Secrets = [
  {
    id: 'r2-analytics',
    name: 'Analytics R2 Bucket',
    description: 'Cloudflare R2 bucket for analytics data storage',
    accessKeyId: 'cf_access_key_example',
    secretAccessKey: 'cf_secret_key_example',
    accountId: 'abc123def456',
    bucketName: 'analytics-data',
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    scope: 'organization',
    organizationId: MOCK_IDS.organization,
  },
];

const mockGcsSecrets = [
  {
    id: 'gcs-data-lake',
    name: 'Data Lake GCS',
    description: 'Google Cloud Storage bucket for data lake',
    gcpProjectId: 'my-gcp-project-123',
    serviceAccountKey: '{"type": "service_account", "project_id": "my-gcp-project-123", ...}',
    bucketName: 'data-lake-bucket',
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    scope: 'organization',
    organizationId: MOCK_IDS.organization,
  },
];

const mockAzureStorageSecrets = [
  {
    id: 'azure-data-lake',
    name: 'Azure Data Lake',
    description: 'Azure Blob Storage for enterprise data lake',
    accountName: 'myazurestorage',
    accountKey: 'base64encodedkeyexample==',
    connectionString: 'DefaultEndpointsProtocol=https;AccountName=myazurestorage;AccountKey=...',
    containerName: 'data-container',
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    scope: 'organization',
    organizationId: MOCK_IDS.organization,
  },
];

const mockPostgresqlSecrets = [
  {
    id: 'pg-warehouse',
    name: 'Data Warehouse',
    description: 'Main PostgreSQL data warehouse for analytics',
    host: 'warehouse.example.com',
    port: 5432,
    database: 'analytics',
    username: 'analytics_user',
    password: '********',
    sslMode: 'require',
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    scope: 'organization',
    organizationId: MOCK_IDS.organization,
  },
];

const mockAdbcSecrets = [
  {
    id: 'adbc-gizmosql',
    name: 'GizmoSQL ADBC',
    description: 'ADBC connection to GizmoSQL cluster via Flight SQL',
    driver: 'flightsql',
    host: 'try-gizmosql-adbc.gizmodata.com',
    port: 31337,
    useTls: true,
    skipTlsVerification: false,
    username: 'adbc_user',
    password: '********',
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    scope: 'organization',
    organizationId: MOCK_IDS.organization,
  },
];

// Mock clusters for demo mode
const mockClusters = [
  {
    id: 'production-db',
    name: 'production-db',
    label: 'Production Database',
    environmentId: MOCK_IDS.environment,
    cloudRegionId: MOCK_IDS.cloudRegion,
    status: 'running',
    databaseEngine: 'duckdb',
    mountedDatabases: [
      { id: 'mount-1', type: 'database-file', name: 'production-db.duckdb', fileName: 'production-db.duckdb' },
      { id: 'mount-2', type: 'catalog', name: 'analytics-lake', catalogName: 'analytics-lake' }
    ],
    mountedSecrets: [
      { id: 'secret-mount-1', secretId: 'aws-s3-prod', secretName: 'Production S3 Bucket', secretType: 'aws-s3', bucketUri: 's3://my-prod-bucket/data' },
      { id: 'secret-mount-2', secretId: 'r2-analytics', secretName: 'Analytics R2 Storage', secretType: 'cloudflare-r2', bucketUri: 'r2://analytics-data' },
      { id: 'secret-mount-3', secretId: 'pg-warehouse', secretName: 'Data Warehouse', secretType: 'postgresql', connectionUri: 'postgresql://warehouse.example.com:5432/analytics' }
    ],
    defaultDatabaseId: 'mount-1',
    plan: 'Pro',
    createdAt: '2024-01-15T10:00:00Z',
    endpoint: 'prod-db.gizmosql.com:5432',
    namespace: 'gizmo-prod-12345',
    tlsEnabled: true,
    ipRestrictions: '0.0.0.0/0',
    autoTerminateEnabled: true,
    autoTerminateMinutes: 120,
    createdByUserId: MOCK_IDS.user,
  },
  {
    id: 'analytics-db',
    name: 'analytics-db',
    label: 'Analytics Database',
    environmentId: MOCK_IDS.environment,
    cloudRegionId: MOCK_IDS.cloudRegion,
    status: 'running',
    databaseEngine: 'duckdb',
    mountedDatabases: [
      { id: 'mount-3', type: 'catalog', name: 'production-lake', catalogName: 'production-lake' }
    ],
    mountedSecrets: [
      { id: 'secret-mount-4', secretId: 'gcs-project-data', secretName: 'Project Data Lake', secretType: 'gcs', bucketUri: 'gs://project-data-lake/datasets' },
      { id: 'secret-mount-5', secretId: 'adbc-gizmosql', secretName: 'GizmoSQL ADBC', secretType: 'adbc', connectionUri: 'grpc+tls://try-gizmosql-adbc.gizmodata.com:31337' }
    ],
    defaultDatabaseId: 'mount-3',
    plan: 'Enterprise',
    createdAt: '2024-02-01T14:30:00Z',
    endpoint: 'analytics-db.gizmosql.com:5432',
    namespace: 'gizmo-analytics-67890',
    tlsEnabled: true,
    ipRestrictions: '192.168.1.0/24',
    autoTerminateEnabled: false,
    autoTerminateMinutes: 60,
    createdByUserId: MOCK_IDS.user,
  },
  {
    id: 'dev-sandbox',
    name: 'dev-sandbox',
    label: 'Development Sandbox',
    environmentId: MOCK_IDS.environment,
    cloudRegionId: MOCK_IDS.cloudRegion,
    status: 'stopped',
    databaseEngine: 'duckdb',
    mountedDatabases: [],
    mountedSecrets: [],
    plan: 'Basic',
    createdAt: '2024-03-01T08:00:00Z',
    endpoint: 'dev-sandbox.gizmosql.com:5432',
    namespace: 'gizmo-dev-22222',
    tlsEnabled: true,
    ipRestrictions: '0.0.0.0/0',
    autoTerminateEnabled: true,
    autoTerminateMinutes: 60,
    createdByUserId: MOCK_IDS.user,
  },
];

/**
 * Check if mock data has already been seeded
 */
export function isMockDataSeeded(): boolean {
  return localStorage.getItem(STORAGE_KEYS.seeded) === 'true';
}

/**
 * Seed mock data if it doesn't exist.
 * This creates the full hierarchy needed for the demo to work.
 */
export function seedMockData(): void {
  // Check if we've already seeded AND data still exists
  const existingOrgs = localStorage.getItem(STORAGE_KEYS.organizations);
  const hasOrgs = existingOrgs && JSON.parse(existingOrgs).length > 0;

  if (isMockDataSeeded() && hasOrgs) {
    console.log('[MockData] Already seeded and data exists, skipping');
    return;
  }

  console.log('[MockData] Seeding mock data for demo mode...');

  // Always seed if we get here (either first time or data was cleared)

  // Seed plans first (organization depends on plans)
  localStorage.setItem(STORAGE_KEYS.plans, JSON.stringify(mockPlans));
  console.log('[MockData] Created mock plans:', mockPlans.length);

  // Seed organization
  localStorage.setItem(STORAGE_KEYS.organizations, JSON.stringify([mockOrganization]));
  localStorage.setItem(STORAGE_KEYS.currentOrganization, JSON.stringify(mockOrganization));
  console.log('[MockData] Created mock organization:', mockOrganization.name);

  // Seed member
  localStorage.setItem(STORAGE_KEYS.members, JSON.stringify([mockMember]));
  console.log('[MockData] Created mock member');

  // Seed project
  localStorage.setItem(STORAGE_KEYS.projects, JSON.stringify([mockProject]));
  console.log('[MockData] Created mock project:', mockProject.name);

  // Seed environment
  localStorage.setItem(STORAGE_KEYS.environments, JSON.stringify([mockEnvironment]));
  console.log('[MockData] Created mock environment:', mockEnvironment.name);

  // Seed clusters
  localStorage.setItem(STORAGE_KEYS.clusters, JSON.stringify(mockClusters));
  console.log('[MockData] Created mock clusters:', mockClusters.length);

  // Seed secrets
  localStorage.setItem(STORAGE_KEYS.secretsAwsS3, JSON.stringify(mockAwsS3Secrets));
  localStorage.setItem(STORAGE_KEYS.secretsCloudflareR2, JSON.stringify(mockCloudflareR2Secrets));
  localStorage.setItem(STORAGE_KEYS.secretsGcs, JSON.stringify(mockGcsSecrets));
  localStorage.setItem(STORAGE_KEYS.secretsAzureStorage, JSON.stringify(mockAzureStorageSecrets));
  localStorage.setItem(STORAGE_KEYS.secretsPostgresql, JSON.stringify(mockPostgresqlSecrets));
  localStorage.setItem(STORAGE_KEYS.secretsAdbc, JSON.stringify(mockAdbcSecrets));
  console.log('[MockData] Created mock secrets for all types');

  // Mark as seeded
  localStorage.setItem(STORAGE_KEYS.seeded, 'true');
  console.log('[MockData] Seeding complete');
}

/**
 * Clear all mock data (for testing/reset)
 */
export function clearMockData(): void {
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
  console.log('[MockData] Cleared all mock data');
}

/**
 * Force re-seed mock data (clears existing and re-seeds)
 */
export function reseedMockData(): void {
  clearMockData();
  seedMockData();
}
